import { useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../ampli';
import { Icon } from '../../../../components/common/icon/Icon';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { usePopup } from '../../../../hooks/popup/usePopup';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../hooks/user/useGetUserProfile';
import { requestLogin } from '../../../../utils/modal/requestLogin.util';
import { toastAdvertismentAgreementResult } from '../../../../utils/modal/toastAdvertismentAgreementResult.util';
import { useAdvertisementConsent } from '../../../../hooks/user/useAdvertisementConsent';

interface MarketAlarmButtonProps {
  pageType: 'PLP' | 'OLP' | 'TLP';
}

export const MarketAlarmButton = ({ pageType }: MarketAlarmButtonProps) => {
  const Popup = usePopup();
  const navigate = useNavigate();

  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();

  const updateAdConsent = useAdvertisementConsent(pageType);

  const [isShowConfirmModal, setIsShowConfirmModal] = useState<boolean>(false);

  const onAlarmContainerClick = () => {
    setIsShowConfirmModal(true);
    ReactGA.event('pvm_click_suggest_alarm_button', {
      isModal: false,
    });
  };

  const handleAdvertisementTermsAgree = () => {
    if (!isUserLoggedIn) {
      requestLogin(Popup, navigate);
    } else {
      updateAdConsent({
        isAgreeing: true,
        onSuccess: () => {
          toastAdvertismentAgreementResult(Popup, true);
        },
      });
    }
  };

  if (userProfile?.advertisementTermsAgreement) {
    return <></>;
  }

  return (
    <>
      <AlarmContainer onClick={onAlarmContainerClick}>
        <TitleSection>
          <BellIconWrapper>
            <Icon name={'Bell2'} width={13} height={13} />
          </BellIconWrapper>
          <Title>{'신규 매물 알람을 받아보세요!'}</Title>
        </TitleSection>
        <Button>
          <ButtonText>{'설정하기'}</ButtonText>
          <Icon name={'ArrowRightSmall'} width={10} height={10} />
        </Button>
      </AlarmContainer>

      <PopupConfirmModal
        isShow={isShowConfirmModal}
        hasCloseButton={false}
        closeModal={() => setIsShowConfirmModal(false)}
        title={'광고성 정보 수신 동의'}
        description={'광고성 정보 수신 동의하시고,\n신규 매물에 대한 알림을 받아 보세요!'}
        buttonArray={[
          {
            text: '광고성 정보 수신 동의하기',
            onClick: handleAdvertisementTermsAgree,
            color: 'blue',
          },
          {
            text: '닫기',
            onClick: () => {
              ampli.pvmClickSetAlarmModalClose({
                page_type: pageType,
              });
              setIsShowConfirmModal(false);
            },
            color: 'gray',
          },
        ]}
      />
    </>
  );
};

const AlarmContainer = styled.div`
  display: flex;
  padding: var(--radius-medium, 8px) var(--radius-large, 16px);
  align-items: center;
  align-self: stretch;
  justify-content: space-between;
  cursor: pointer;

  background: var(--Light-Scheme-Monochrome-black, #232429);
`;

const BellIconWrapper = styled.div`
  display: flex;
  width: 18px;
  height: 18px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: var(--radius-large, 16px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);
`;

const TitleSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--radius-medium, 8px);
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const Button = styled.button`
  display: flex;
  padding: 6px 8px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const ButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;
